/**
 * クロスオリジン対応のファイルダウンロードユーティリティ
 *
 * - Content-Disposition: attachment 付き presigned URL（BE の download_url）は
 *   <a> ナビゲーションでブラウザ標準のダウンロードに直行する（Blob 全量取得を
 *   待たないため即開始・進捗はブラウザの DL UI に表示・ファイル名はヘッダで決定）。
 * - それ以外の URL は <a download> がクロスオリジンで無視されるため、
 *   fetch → Blob → Object URL 経由で強制ダウンロードする。CORS 起因で fetch が
 *   失敗する場合は XMLHttpRequest でも試行する。
 */

/** HTTP ステータス付きのダウンロード失敗（URL 失効・404 等）。 */
export class DownloadHttpError extends Error {
  status: number;

  constructor(status: number) {
    super(`Download failed: ${status}`);
    this.name = 'DownloadHttpError';
    this.status = status;
  }
}

/**
 * URL からファイルを Blob として取得する。
 *
 * fetch を試み、CORS/ネットワーク起因で失敗したら XMLHttpRequest にフォールバック
 * する。HTTP エラー（403/404 等）はリトライしても同じ結果のため即座に
 * `DownloadHttpError` を投げる。どちらも失敗した場合は例外を投げる
 * （呼び出し側でフォールバック処理を行う）。
 * 単体ダウンロードと zip 一括ダウンロードで共通利用する。
 */
export async function fetchFileAsBlob(url: string): Promise<Blob> {
  // 1. fetch + Blob 方式（CORS 対応の URL で動作）
  // cache: "no-store" で、先に <audio>/<img> 等が no-cors で取得した
  // opaque レスポンス（CORS ヘッダ無し）のキャッシュ再利用による CORS エラーを回避する。
  try {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) throw new DownloadHttpError(response.status);
    return await response.blob();
  } catch (error) {
    // HTTP エラーは XHR でも同じステータスが返るだけなので即座に諦める
    if (error instanceof DownloadHttpError) throw error;
    // fetch 失敗（CORS/ネットワーク）→ XHR にフォールバック
  }

  // 2. XMLHttpRequest 方式（fetch が失敗する環境でも動作する場合がある）
  return xhrDownload(url);
}

/**
 * URL が Content-Disposition: attachment 付き（BE が発行する download_url）か。
 * この URL は <a> ナビゲーションだけでブラウザ標準のダウンロードになる。
 */
function hasAttachmentDisposition(url: string): boolean {
  try {
    const disposition = new URL(url).searchParams.get('response-content-disposition');
    return disposition?.startsWith('attachment') ?? false;
  } catch {
    return false;
  }
}

/**
 * URL の有効性をヘッダのみで確認する（本文は転送開始直後に打ち切る）。
 * 失効 presigned URL（400/403）で <a> ナビゲーションするとエラー XML への
 * ページ遷移になるため、その事前チェック用。
 */
async function probeUrlOk(url: string): Promise<boolean> {
  const controller = new AbortController();
  const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
  const ok = response.ok;
  controller.abort();
  return ok;
}

/** <a> タグでブラウザ標準のダウンロードを起動する（ページ遷移なし）。 */
function triggerAnchorDownload(url: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  // クリック直後に同期で remove すると、ブラウザによってはナビゲーション/
  // ダウンロードが取り消されることがあるため遅延して掃除する。
  setTimeout(() => {
    if (link.parentNode) link.parentNode.removeChild(link);
  }, 1000);
}

/**
 * URL のファイルをダウンロードする。
 *
 * - attachment 付き URL（download_url）: ヘッダのみの事前チェック後、
 *   <a> ナビゲーションでブラウザ標準のダウンロードに直行する。
 *   Blob 全量取得を待たないため、クリック後すぐダウンロードが開始され、
 *   進捗もブラウザのダウンロード UI に表示される。ファイル名は
 *   Content-Disposition（filename*）で正しく付く。
 * - それ以外の URL: fetch/XHR で Blob を取得してファイル名付きで保存し、
 *   CORS 等で Blob 化できない場合のみ <a download> にフォールバックする。
 * - HTTP エラー（URL 失効・404 等）が確認できた場合は false を返す
 *   （呼び出し側でエラー表示する。<a> フォールバックはエラーページへの
 *   遷移になるため行わない）。
 */
export async function downloadFile(url: string, fileName: string): Promise<boolean> {
  if (hasAttachmentDisposition(url)) {
    try {
      if (!(await probeUrlOk(url))) {
        console.warn(`[downloadFile] probe failed for ${url}`);
        return false;
      }
    } catch {
      // 事前チェックが CORS/ネットワーク起因で失敗しても、attachment URL は
      // <a> ナビゲーションで保存できる可能性が高いためそのまま試行する
    }
    triggerAnchorDownload(url, fileName);
    return true;
  }

  try {
    const blob = await fetchFileAsBlob(url);
    triggerBlobDownload(blob, fileName);
    return true;
  } catch (error) {
    if (error instanceof DownloadHttpError) {
      console.warn(`[downloadFile] HTTP ${error.status} for ${url}`);
      return false;
    }
    // Blob 取得に失敗（CORS 等）→ <a> タグ方式にフォールバック
  }

  triggerAnchorDownload(url, fileName);
  return true;
}

/**
 * ダウンロードに使う URL をファイルオブジェクトから解決する。
 *
 * `download_url`（attachment 付き presigned URL）を最優先し、無ければ
 * `original_url` にフォールバックする。どちらも無い場合は null。
 */
export function resolveDownloadUrl(file: {
  download_url?: string | null;
  original_url?: string | null;
}): string | null {
  return file.download_url || file.original_url || null;
}

/** ダウンロード失敗時にユーザーへ表示する共通メッセージ。 */
export const DOWNLOAD_FAILED_MESSAGE =
  'ダウンロードできませんでした。ページを再読み込みして再度お試しください';

/**
 * メディアプレビュー共通のダウンロード処理。
 *
 * downloadUrl（元ファイル）を優先し、未指定時は再生用 src にフォールバックする。
 * 再生用 src は変換後ファイル（オーディオ: m4a / 動画: HLS）を指す場合があるため、
 * 元ファイルを保存させたい呼び出し側は必ず downloadUrl を渡すこと。
 *
 * @returns ダウンロードを開始した場合は true（URL が無い場合は false）
 */
export function downloadMediaFile(options: {
  /** 元ファイルのURL（優先して使用） */
  downloadUrl?: string;
  /** 再生用URL（downloadUrl 未指定時のフォールバック） */
  src?: string;
  /** 保存ファイル名（未指定時は URL 末尾 → fallbackName） */
  fileName?: string;
  /** ファイル名が決定できない場合の最終フォールバック名 */
  fallbackName: string;
}): boolean {
  const url = options.downloadUrl || options.src;
  if (!url) return false;
  void downloadFile(url, options.fileName || url.split('/').pop() || options.fallbackName);
  return true;
}

export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
}

function xhrDownload(url: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as Blob);
      } else {
        reject(new DownloadHttpError(xhr.status));
      }
    };
    xhr.onerror = () => reject(new Error('XHR network error'));
    xhr.send();
  });
}

/**
 * URL からファイルをバイト列（Uint8Array）として取得する。
 *
 * `fetchFileAsBlob` と違い Blob を経由せず、レスポンスボディをストリームで
 * 読んでメモリ上に連結する。ffmpeg.wasm への入力のように最終的にバイト列が
 * 必要な用途向けで、Blob → ArrayBuffer のコピーが 1 回減るうえ、
 * `response.blob()` / XHR の `responseType: 'blob'` がブラウザの Blob ストレージ
 * 都合で失敗する環境（組み込みブラウザ等）でも取得できる。
 * HTTP エラーは `DownloadHttpError`。
 */
export async function fetchFileAsBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new DownloadHttpError(response.status);
  if (!response.body) return new Uint8Array(await response.arrayBuffer());

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    total += value.byteLength;
  }
  if (chunks.length === 1) return chunks[0];
  const bytes = new Uint8Array(total);
  let offset = 0;
  chunks.forEach(chunk => {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  });
  return bytes;
}
